import type { LayoutServiceData } from '@sitecore-jss/sitecore-jss-vue';
import { SitecoreJssPlaceholderPlugin } from '@sitecore-jss/sitecore-jss-vue';
import { createHead as createClientHead } from '@unhead/vue/client';
import { createHead as createServerHead } from '@unhead/vue/server';
import { createPinia } from 'pinia';
import { createPersistedState } from 'pinia-plugin-persistedstate';
import { createApp as createClientApp, createSSRApp, h } from 'vue';
import AppRoot from './AppRoot.vue';
import createRouter from './create-router';
import useSitecoreStore from './stores/sitecore';
import componentFactory from './temp/componentFactory';
import type { AppContext } from './types';

const createApp = (initialState: LayoutServiceData, appContext: AppContext, isSSR?: boolean) => {
    const pinia = createPinia();
    const router = createRouter(isSSR, appContext);
    const vueOptions = { router, render: () => h(AppRoot) };
    const app = isSSR ? createSSRApp(vueOptions) : createClientApp(vueOptions);
    const head = isSSR ? createServerHead() : createClientHead();

    if (!isSSR) {
        pinia.use(
            createPersistedState({
                key: id => `product-pages_${id}`,
                storage: sessionStorage
            })
        );
    }

    app.use(router);
    app.use(head);
    app.use(pinia);
    app.use(SitecoreJssPlaceholderPlugin, { componentFactory });

    const sitecoreStore = useSitecoreStore();

    sitecoreStore.setAppContext(appContext);

    // If there is an initial state defined, push it into the store, where it can be referenced by interested components.
    if (initialState) {
        sitecoreStore.setSitecoreData(initialState);
    }

    return { app, head, router };
};

export default createApp;
